import React, { useEffect } from "react";
import {
  TrendingUp,
  FileText,
  Lightbulb,
  Briefcase,
  Heart,
  BookOpen,
} from 'lucide-react';
import { learningGoals as goals } from "src/constants/learningGoals";

interface LearningGoalsProps {
  learningGoals: string[];
  setLearningGoals: (goals: string[]) => void;
}

interface Goal {
  id: string;
  label: string;
  icon: React.FC<any>;
  color: string;
}

const LearningGoals: React.FC<LearningGoalsProps> = ({
  learningGoals,
  setLearningGoals,
}) => {
  // Load Nunito font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;600&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    const scrollToTop = () => {
      try {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        
        if (window.scrollTo) {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
          });
        }
        
        requestAnimationFrame(() => {
          window.scrollTo(0, 0);
        });
      } catch (error) {
        console.warn('ScrollToTop failed:', error);
      }
    };

    scrollToTop();

    const timeouts = [
      setTimeout(scrollToTop, 0),
      setTimeout(scrollToTop, 10),
      setTimeout(scrollToTop, 50),
      setTimeout(scrollToTop, 100),
      setTimeout(scrollToTop, 200)
    ];

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  const toggleGoal = (goalId: string) => {
    if (learningGoals.includes(goalId)) {
      setLearningGoals(learningGoals.filter((id) => id !== goalId));
    } else {
      setLearningGoals([...learningGoals, goalId]);
    }
  };

  const Hexagon: React.FC<{
    goal: Goal;
    selected: boolean;
    onToggle: () => void;
  }> = ({ goal, selected, onToggle }) => {
    const Icon = goal.icon;
    const hexColor = selected ? goal.color : `${goal.color}CC`;

    return (
      <div
        onClick={onToggle}
        className="relative w-[182px] h-[161px] cursor-pointer transition-transform duration-200 hover:scale-[1.05]"
        style={{ fontFamily: 'Nunito, sans-serif' }}
      >
        <div
          className="w-full h-full flex flex-col items-center justify-center text-white transition-colors duration-300 ease-in-out"
          style={{
            clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
            backgroundColor: hexColor,
          }}
        >
          <Icon size={28} className="mb-1 -mt-4" />
          <span className="text-center text-[16px] leading-tight font-[400] px-2">
            {goal.label}
          </span>
        </div>

        {selected && (
          <div
            className="absolute top-[10px] right-[12px] bg-white text-black rounded-full w-5 h-5 flex items-center justify-center text-[13px] font-bold shadow-md z-10"
          >
            ✓
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white pt-8 pb-4 flex flex-col min-h-0" style={{ fontFamily: 'Nunito, sans-serif' }}>
      {/* Header */}
      <div className="text-center mb-8">
        <h1
          className="text-black mb-2"
          style={{ fontSize: '48px', fontWeight: 400, lineHeight: '1.2' }}
        >
          What are your learning goals?
        </h1>
        <p className="text-green-600 text-[16px] font-[400]">
          ✓ Select all that apply to you.
        </p>
      </div>

      {/* Hexagon Grid */}
      <div className="flex flex-col items-center justify-center gap-y-20 flex-1">
        {/* Row 1 */}
        <div className="flex gap-x-20">
          <Hexagon goal={goals[0]} selected={learningGoals.includes(goals[0].id)} onToggle={() => toggleGoal(goals[0].id)} />
          <Hexagon goal={goals[1]} selected={learningGoals.includes(goals[1].id)} onToggle={() => toggleGoal(goals[1].id)} />
          <Hexagon goal={goals[2]} selected={learningGoals.includes(goals[2].id)} onToggle={() => toggleGoal(goals[2].id)} />
        </div>

        {/* Row 2 */}
        <div className="flex gap-x-20 mt-[-48px]">
          <Hexagon goal={goals[3]} selected={learningGoals.includes(goals[3].id)} onToggle={() => toggleGoal(goals[3].id)} />
          <Hexagon goal={goals[4]} selected={learningGoals.includes(goals[4].id)} onToggle={() => toggleGoal(goals[4].id)} />
        </div>

        {/* Row 3 */}
        <div className="flex gap-x-4 mt-[-48px]">
          <Hexagon goal={goals[5]} selected={learningGoals.includes(goals[5].id)} onToggle={() => toggleGoal(goals[5].id)} />
        </div>
      </div>

      {/* Navigation Buttons Wrapper (if present in this component) */}
      <div className="flex justify-between items-center mt-8 px-8">
        {/* Navigation buttons go here if needed */}
      </div>
    </div>
  );
};

export default LearningGoals;
